import createCache, { type EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider } from '@mui/system';
import rawr2wc from '@r2wc/react-to-web-component';
import type { R2WCOptions } from '@r2wc/core';
import merge from 'lodash-es/merge';
import React from 'react';
import { theme } from './theme';

// (1) create the context
export const ShadowRootContext = React.createContext<ShadowRoot | null>(null);

export function r2wc<P extends object>(
  Component: React.ComponentType<P>,
  propsSchema?: Record<
    string,
    'string' | 'number' | 'boolean' | 'json' | 'function'
  >
) {
  // 1) make one style-element that Emotion will target
  const styleContainer = document.createElement('style');

  // 2) wrap your component tree in CacheProvider using that container
  const Wrapped: React.FC<P> = (props: P) => {
    // create the cache once
    const cache = React.useMemo<EmotionCache>(
      () =>
        createCache({
          key: 'css',
          container: styleContainer,
          prepend: true,
        }),
      []
    );

    // if you need your MUI popovers/modals/etc to render into the shadow root as well,
    // override their `container` defaultProp to the host’s shadowRoot:
    const shadowTheme = React.useMemo(
      () =>
        merge({}, theme, {
          components: {
            MuiPopover: {
              defaultProps: {
                container: styleContainer.getRootNode() as ShadowRoot,
              },
            },
            MuiPopper: {
              defaultProps: {
                container: styleContainer.getRootNode() as ShadowRoot,
              },
            },
            MuiModal: {
              defaultProps: {
                container: styleContainer.getRootNode() as ShadowRoot,
              },
            },
          },
        }),
      []
    );

    return (
      <ShadowRootContext value={styleContainer.getRootNode() as ShadowRoot}>
        <CacheProvider value={cache}>
          <ThemeProvider theme={shadowTheme}>
            <Component {...props} />
          </ThemeProvider>
        </CacheProvider>
      </ShadowRootContext>
    );
  };

  // 3) convert it to a CE, but in connectedCallback append that style node
  const r2wcOpts: R2WCOptions<P> = { shadow: 'open' as const };
  if (propsSchema) {
    r2wcOpts.props = propsSchema;
  }

  const BaseCE = rawr2wc(Wrapped, r2wcOpts);
  return class CEWithEmotion extends BaseCE {
    connectedCallback() {
      // @ts-expect-error connectedCallback works fine
      super.connectedCallback?.();
      // now that shadowRoot exists, append the style element into it
      this.shadowRoot?.appendChild(styleContainer);
    }
  };
}
